import React, { useState, useEffect, useCallback } from "react";
import {
    Box,
    Typography,
    Button,
    Chip,
    IconButton,
    Link as MuiLink,
    Menu,
    MenuItem,
    Paper,
    Stack,
    CircularProgress,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from "@mui/material";
import {
    Add as AddIcon,
    AutoAwesome as AutoAwesomeIcon,
    DeleteOutline as DeleteOutlineIcon,
    EditOutlined as EditOutlinedIcon,
    OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import { FormattedMessage, useIntl } from "react-intl";
import { useSnackbar } from "notistack";
import insightsApi from "../../api/insightsApi";
import { roleRefusalMessage } from "../../utils/projectRoles";
import ActivityForm from "./ActivityForm";
import SuggestionVerdict from "./SuggestionVerdict";
import {
    ACTIVITY_STATUSES,
    STATUS_BADGE,
    asActivities,
    evidenceHref,
    formatDate,
    isSuggested,
    normalizeStatus,
    statusLabel,
} from "./activityConstants";

const sectionLabelSx = {
    fontSize: "0.75rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    color: "text.secondary",
    textTransform: "uppercase",
};

// The status chip IS the quick status change: the thing you read to learn where
// an activity has got to is the thing you click to move it. A separate control
// would mean two places saying the same word. Without `onChange` (a project
// viewer) it is just the badge.
function StatusChip({ value, onChange, busy }) {
    const intl = useIntl();
    const [anchor, setAnchor] = useState(null);
    const key = normalizeStatus(value);
    const meta = STATUS_BADGE[key];

    if (!onChange) {
        return (
            <Chip
                size="small"
                label={statusLabel(intl, key)}
                sx={{ backgroundColor: meta.bg, color: meta.color, fontWeight: 600 }}
            />
        );
    }

    return (
        <>
            <Chip
                size="small"
                label={statusLabel(intl, key)}
                disabled={busy}
                onClick={(e) => setAnchor(e.currentTarget)}
                // The chip's own name says the status; the title says what
                // clicking it does, which the name alone never implies.
                title={intl.formatMessage({
                    id: "activity.change_status",
                    defaultMessage: "Change status",
                })}
                sx={{
                    backgroundColor: meta.bg,
                    color: meta.color,
                    fontWeight: 600,
                    "&:hover": { backgroundColor: meta.bg, filter: "brightness(0.95)" },
                }}
            />
            <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
                {ACTIVITY_STATUSES.map((option) => (
                    <MenuItem
                        key={option}
                        selected={option === key}
                        onClick={() => {
                            setAnchor(null);
                            if (option !== key) onChange(option);
                        }}
                    >
                        {statusLabel(intl, option)}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}

// One activity. Deliberately a stack of lines rather than a table row: this lives
// in a drawer, and the fields that matter (dates, evidence) are the ones most
// often empty, so a fixed grid would be mostly blank columns.
// `readOnly` (a project viewer) leaves the row's facts and drops every control.
function ActivityRow({
    activity, busy, onStatusChange, onAccept, onReject, onEdit, onDelete, readOnly = false,
}) {
    const intl = useIntl();
    const href = evidenceHref(activity.evidence);
    const planned = formatDate(intl, activity.planned_date);
    const completed = formatDate(intl, activity.completed_date);
    const suggested = isSuggested(activity);

    return (
        <Paper
            variant="outlined"
            sx={{
                p: 1.5,
                borderRadius: 2,
                // A proposal waiting on a person is visibly not the same thing as
                // a plan — the badge says so, and the tinted edge means you can
                // see which rows still need you without reading them.
                ...(suggested
                    ? { borderColor: "rgba(0,67,142,0.35)", backgroundColor: "rgba(0,67,142,0.03)" }
                    : {}),
            }}
        >
            <Stack direction="row" spacing={1} alignItems="flex-start">
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {activity.title}
                        </Typography>
                        <StatusChip
                            value={activity.status}
                            busy={busy}
                            onChange={readOnly ? undefined : (next) => onStatusChange(activity, next)}
                        />
                        {suggested && (
                            <Chip
                                size="small"
                                icon={<AutoAwesomeIcon />}
                                label={intl.formatMessage({
                                    id: "activity.suggested",
                                    defaultMessage: "Suggested",
                                })}
                                sx={{
                                    backgroundColor: "rgba(0,67,142,0.10)",
                                    color: "#00438E",
                                    fontWeight: 600,
                                    "& .MuiChip-icon": { color: "#00438E", fontSize: "0.85rem" },
                                }}
                            />
                        )}
                    </Stack>

                    {activity.description && (
                        <Typography
                            variant="caption"
                            sx={{ color: "text.secondary", display: "block", mt: 0.5, whiteSpace: "pre-wrap" }}
                        >
                            {activity.description}
                        </Typography>
                    )}

                    {(planned || completed) && (
                        <Stack direction="row" spacing={1.5} sx={{ mt: 0.75 }} flexWrap="wrap" useFlexGap>
                            {planned && (
                                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                    <FormattedMessage
                                        id="activity.planned_on"
                                        defaultMessage="Planned {date}"
                                        values={{ date: planned }}
                                    />
                                </Typography>
                            )}
                            {completed && (
                                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                    <FormattedMessage
                                        id="activity.completed_on"
                                        defaultMessage="Completed {date}"
                                        values={{ date: completed }}
                                    />
                                </Typography>
                            )}
                        </Stack>
                    )}

                    {activity.evidence && (
                        <Box sx={{ mt: 0.75 }}>
                            {href ? (
                                // noreferrer as well as noopener: an evidence link
                                // is someone else's URL and has no business
                                // learning where it was clicked from.
                                <MuiLink
                                    href={href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    variant="caption"
                                    sx={{ display: "inline-flex", alignItems: "center", gap: 0.25, color: "#00438E" }}
                                >
                                    {activity.evidence}
                                    <OpenInNewIcon sx={{ fontSize: "0.85rem" }} />
                                </MuiLink>
                            ) : (
                                <Typography
                                    variant="caption"
                                    sx={{ color: "text.secondary", fontStyle: "italic", whiteSpace: "pre-wrap" }}
                                >
                                    {activity.evidence}
                                </Typography>
                            )}
                        </Box>
                    )}
                </Box>

                {/* A proposal is answered before it is managed: the verdict
                    pair sits ahead of the row's own controls, and stands in for
                    the delete icon below — rejecting IS the delete. */}
                {suggested && !readOnly && (
                    <SuggestionVerdict
                        title={activity.title}
                        busy={busy}
                        onAccept={() => onAccept(activity)}
                        onReject={() => onReject(activity)}
                    />
                )}

                {!readOnly && (
                <Stack direction="row" sx={{ flexShrink: 0 }}>
                    <IconButton
                        size="small"
                        disabled={busy}
                        onClick={() => onEdit(activity)}
                        aria-label={intl.formatMessage(
                            { id: "activity.edit_named", defaultMessage: "Edit {title}" },
                            { title: activity.title }
                        )}
                    >
                        <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                    {!suggested && (
                        <IconButton
                            size="small"
                            disabled={busy}
                            onClick={() => onDelete(activity)}
                            aria-label={intl.formatMessage(
                                { id: "activity.delete_named", defaultMessage: "Delete {title}" },
                                { title: activity.title }
                            )}
                        >
                            <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                    )}
                </Stack>
                )}
            </Stack>
        </Paper>
    );
}

// The execution view for one impact: what is planned against it, where each thing
// has got to, and the evidence it happened.
//
// `onChanged` lets the host know the counts moved, so the dashboard it may also
// be showing does not go stale behind this panel.
//
// `compact` drops the section heading for an embedded use (the dashboard expands
// a row into one of these) — there the row above already names the impact, so
// the word "Activities" a second time labels nothing.
export default function ActivityPanel({
    projectId,
    impact,
    onChanged,
    compact = false,
    // False for a project viewer: the activities are listed, nothing changes them.
    canEdit = true,
}) {
    const intl = useIntl();
    const { enqueueSnackbar } = useSnackbar();
    const impactId = impact?.id;

    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    // `null` = the form is closed; `{}` = adding; `{ activity }` = editing.
    const [editor, setEditor] = useState(null);
    const [saving, setSaving] = useState(false);
    const [busyId, setBusyId] = useState(null);
    const [pendingDelete, setPendingDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    // The AI call is an LLM round-trip, so it gets its own visible wait rather
    // than the generic row spinner.
    const [suggesting, setSuggesting] = useState(false);
    const [loadedOnce, setLoadedOnce] = useState(false);

    const load = useCallback(async () => {
        if (!projectId || !impactId) return;
        try {
            setLoading(true);
            setLoadError(false);
            const data = await insightsApi.getActivities(projectId, impactId);
            setActivities(asActivities(data));
        } catch (error) {
            console.error("Failed to load activities:", error);
            setLoadError(true);
        } finally {
            setLoading(false);
            setLoadedOnce(true);
        }
    }, [projectId, impactId]);

    useEffect(() => {
        load();
    }, [load]);

    // DRF answers a rejected field with { field: [message] }. Surfacing the first
    // beats a blank "failed" when the BE turns down a date.
    const detailFrom = (error) => {
        // A role refusal's body is a code, not a sentence.
        const refused = roleRefusalMessage(error, intl);
        if (refused) return refused;
        const data = error?.response?.data;
        if (!data || typeof data !== "object" || Array.isArray(data)) return null;
        return Object.values(data).flat().find((m) => typeof m === "string") || null;
    };

    const handleSave = async (payload) => {
        const existing = editor?.activity;
        try {
            setSaving(true);
            const saved = existing
                ? await insightsApi.updateActivity(projectId, impactId, existing.id, payload)
                : await insightsApi.createActivity(projectId, impactId, payload);
            setActivities((prev) =>
                existing
                    ? prev.map((row) => (row.id === existing.id ? saved : row))
                    : [...prev, saved]
            );
            setEditor(null);
            onChanged?.();
            enqueueSnackbar(
                intl.formatMessage({ id: "activity.saved", defaultMessage: "Activity saved" }),
                { variant: "success" }
            );
        } catch (error) {
            console.error("Failed to save activity:", error);
            enqueueSnackbar(
                detailFrom(error) ||
                    intl.formatMessage({
                        id: "activity.save_failed",
                        defaultMessage: "Failed to save the activity",
                    }),
                { variant: "error" }
            );
        } finally {
            setSaving(false);
        }
    };

    // The quick status change. Only `status` is sent: a PATCH that also carried
    // the dates would overwrite whatever someone else had just set them to.
    const handleStatusChange = async (activity, status) => {
        try {
            setBusyId(activity.id);
            const saved = await insightsApi.updateActivity(projectId, impactId, activity.id, { status });
            setActivities((prev) => prev.map((row) => (row.id === activity.id ? saved : row)));
            onChanged?.();
        } catch (error) {
            console.error("Failed to change the activity status:", error);
            enqueueSnackbar(
                detailFrom(error) ||
                    intl.formatMessage({
                        id: "activity.status_change_failed",
                        defaultMessage: "Failed to change the status",
                    }),
                { variant: "error" }
            );
        } finally {
            setBusyId(null);
        }
    };

    // Accepting a proposal. `approved` alone, like the status change: the row is
    // being accepted as written, not rewritten.
    const handleAccept = async (activity) => {
        try {
            setBusyId(activity.id);
            const saved = await insightsApi.updateActivity(projectId, impactId, activity.id, {
                approved: true,
            });
            setActivities((prev) => prev.map((row) => (row.id === activity.id ? saved : row)));
            onChanged?.();
            enqueueSnackbar(
                intl.formatMessage({ id: "activity.accepted", defaultMessage: "Suggestion accepted" }),
                { variant: "success" }
            );
        } catch (error) {
            console.error("Failed to accept activity:", error);
            enqueueSnackbar(
                detailFrom(error) ||
                    intl.formatMessage({
                        id: "activity.accept_failed",
                        defaultMessage: "Couldn't accept the suggestion",
                    }),
                { variant: "error" }
            );
        } finally {
            setBusyId(null);
        }
    };

    // Rejecting one. The same DELETE the bin does on a normal row, but straight
    // through: no confirmation, because this IS the review step, the row was
    // never anyone's plan, and the AI will propose again.
    const handleReject = async (activity) => {
        try {
            setBusyId(activity.id);
            await insightsApi.deleteActivity(projectId, impactId, activity.id);
            setActivities((prev) => prev.filter((row) => row.id !== activity.id));
            onChanged?.();
            enqueueSnackbar(
                intl.formatMessage({ id: "activity.rejected", defaultMessage: "Suggestion rejected" }),
                { variant: "success" }
            );
        } catch (error) {
            console.error("Failed to reject activity:", error);
            enqueueSnackbar(
                detailFrom(error) ||
                    intl.formatMessage({
                        id: "activity.reject_failed",
                        defaultMessage: "Couldn't reject the suggestion",
                    }),
                { variant: "error" }
            );
        } finally {
            setBusyId(null);
        }
    };

    // Ask the AI for activities against THIS impact. The endpoint persists what it
    // proposes as unapproved rows and hands them back, so the list is refetched
    // rather than patched from the response — that keeps one ordering (the BE's)
    // and one shape.
    const handleSuggest = async () => {
        try {
            setSuggesting(true);
            const result = await insightsApi.suggestActivities(projectId, impactId);
            const created = Number(result?.created_count) || 0;
            await load();
            onChanged?.();
            enqueueSnackbar(
                created > 0
                    ? intl.formatMessage(
                          {
                              id: "activity.ai_created",
                              defaultMessage:
                                  "AI suggested {count, number} {count, plural, one {activity} other {activities}} — accept or reject each one",
                          },
                          { count: created }
                      )
                    : intl.formatMessage({
                          id: "activity.ai_none",
                          defaultMessage: "The AI didn't suggest anything to add here.",
                      }),
                { variant: created > 0 ? "success" : "info" }
            );
        } catch (error) {
            console.error("Failed to get AI activity suggestions:", error);
            enqueueSnackbar(
                detailFrom(error) ||
                    intl.formatMessage({
                        id: "activity.ai_failed",
                        defaultMessage: "The AI couldn't suggest activities right now.",
                    }),
                { variant: "error" }
            );
        } finally {
            setSuggesting(false);
        }
    };

    const handleDelete = async () => {
        if (!pendingDelete) return;
        try {
            setDeleting(true);
            await insightsApi.deleteActivity(projectId, impactId, pendingDelete.id);
            setActivities((prev) => prev.filter((row) => row.id !== pendingDelete.id));
            setPendingDelete(null);
            onChanged?.();
            enqueueSnackbar(
                intl.formatMessage({ id: "activity.deleted", defaultMessage: "Activity deleted" }),
                { variant: "success" }
            );
        } catch (error) {
            console.error("Failed to delete activity:", error);
            enqueueSnackbar(
                roleRefusalMessage(error, intl) ||
                intl.formatMessage({
                    id: "activity.delete_failed",
                    defaultMessage: "Failed to delete the activity",
                }),
                { variant: "error" }
            );
        } finally {
            setDeleting(false);
        }
    };

    return (
        <Box>
            <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                useFlexGap
                sx={{ mb: 2 }}
            >
                {compact ? (
                    <Box />
                ) : (
                    <Typography sx={sectionLabelSx}>
                        <FormattedMessage id="activity.section" defaultMessage="Activities" />
                    </Typography>
                )}

                {canEdit && (
                <Stack direction="row" spacing={1}>
                    {/* describeChild so the tooltip stays a description and the
                        label remains the button's accessible name. */}
                    <Tooltip
                        describeChild
                        title={intl.formatMessage({
                            id: "activity.ai_suggest_help",
                            defaultMessage:
                                "Ask the AI to propose activities for this impact. You accept or reject each one.",
                        })}
                    >
                        {/* A span so the tooltip still has a hoverable child while
                            the button is disabled mid-call. */}
                        <span>
                            <Button
                                size="small"
                                variant="outlined"
                                disabled={suggesting}
                                startIcon={
                                    suggesting ? (
                                        <CircularProgress size={14} sx={{ color: "#00438E" }} />
                                    ) : (
                                        <AutoAwesomeIcon />
                                    )
                                }
                                onClick={handleSuggest}
                                sx={{ color: "#00438E", borderColor: "#00438E" }}
                            >
                                {suggesting ? (
                                    <FormattedMessage
                                        id="activity.ai_working"
                                        defaultMessage="Asking AI…"
                                    />
                                ) : (
                                    <FormattedMessage
                                        id="activity.ai_suggest"
                                        defaultMessage="AI suggest"
                                    />
                                )}
                            </Button>
                        </span>
                    </Tooltip>

                    <Button
                        size="small"
                        variant="contained"
                        startIcon={<AddIcon />}
                        disabled={Boolean(editor)}
                        onClick={() => setEditor({})}
                        sx={{ backgroundColor: "#00438E", "&:hover": { backgroundColor: "#003A75" } }}
                    >
                        <FormattedMessage id="activity.add" defaultMessage="Add activity" />
                    </Button>
                </Stack>
                )}
            </Stack>

            {editor && (
                <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                    <Typography sx={{ ...sectionLabelSx, mb: 2 }}>
                        {editor.activity ? (
                            <FormattedMessage id="activity.edit" defaultMessage="Edit activity" />
                        ) : (
                            <FormattedMessage id="activity.new" defaultMessage="New activity" />
                        )}
                    </Typography>
                    <ActivityForm
                        // Remount per target so the form never inherits the last
                        // row's values.
                        key={editor.activity?.id ?? "new"}
                        activity={editor.activity}
                        saving={saving}
                        onSave={handleSave}
                        onCancel={() => setEditor(null)}
                    />
                </Paper>
            )}

            {/* First load only: a reload after a save or an AI suggestion keeps
                the list in place rather than collapsing the drawer to a spinner
                and losing its scroll position. */}
            {loading && !loadedOnce ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress size={24} sx={{ color: "#00438E" }} />
                </Box>
            ) : loadError ? (
                <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, textAlign: "center" }}>
                    <Typography variant="body2" sx={{ color: "text.secondary", mb: 1.5 }}>
                        <FormattedMessage
                            id="activity.load_failed"
                            defaultMessage="Couldn't load the activities for this impact."
                        />
                    </Typography>
                    <Button size="small" onClick={load} sx={{ color: "#00438E" }}>
                        <FormattedMessage id="impact.ai_retry" defaultMessage="Retry" />
                    </Button>
                </Paper>
            ) : activities.length === 0 ? (
                <Paper
                    variant="outlined"
                    sx={{ p: 3, borderRadius: 2, textAlign: "center", color: "text.secondary" }}
                >
                    <Typography variant="body2">
                        <FormattedMessage
                            id="activity.none"
                            defaultMessage="Nothing planned against this impact yet."
                        />
                    </Typography>
                </Paper>
            ) : (
                // One flat list, in the order they were added: an activity is
                // tracked by its name, not filed under a category.
                <Stack spacing={1}>
                    {activities.map((activity) => (
                        <ActivityRow
                            key={activity.id}
                            activity={activity}
                            busy={busyId === activity.id}
                            onStatusChange={handleStatusChange}
                            onAccept={handleAccept}
                            onReject={handleReject}
                            onEdit={(row) => setEditor({ activity: row })}
                            onDelete={(row) => setPendingDelete(row)}
                            readOnly={!canEdit}
                        />
                    ))}
                </Stack>
            )}

            <Dialog open={Boolean(pendingDelete)} onClose={() => (deleting ? null : setPendingDelete(null))}>
                <DialogTitle>
                    <FormattedMessage id="activity.delete_title" defaultMessage="Delete activity" />
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        <FormattedMessage
                            id="activity.delete_confirm"
                            defaultMessage="Delete “{title}”? This can't be undone."
                            values={{ title: pendingDelete?.title || "" }}
                        />
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setPendingDelete(null)}
                        disabled={deleting}
                        sx={{ color: "text.secondary" }}
                    >
                        <FormattedMessage id="general.cancel" defaultMessage="Cancel" />
                    </Button>
                    <Button onClick={handleDelete} disabled={deleting} color="error" variant="contained">
                        <FormattedMessage id="general.delete" defaultMessage="Delete" />
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
